import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { HelmetProvider } from 'react-helmet-async';
import { router } from './router';
import { queryClient } from './lib/query-client';
import { ToastProvider } from './toast/ToastProvider';
import { UpgradePromptDialog } from './features/billing/UpgradePromptDialog';
import { initAnalytics } from './lib/analytics';
import { initGleap } from './lib/gleap';
import { GleapIdentitySync } from './features/auth/GleapIdentitySync';
import { EMAIL_ACCOUNTS_LIST_GC_MS, emailAccountKeys } from './features/email-accounts/queries';
import './toast/toast.css';
import './styles/global.css';
import './styles/gleap-overrides.css';

const container = document.getElementById('root');
if (!container) throw new Error('#root not found');

initAnalytics();
initGleap();
// See `EMAIL_ACCOUNTS_LIST_GC_MS` for why this is a key-prefix default.
queryClient.setQueryDefaults(emailAccountKeys.lists(), { gcTime: EMAIL_ACCOUNTS_LIST_GC_MS });

createRoot(container).render(
  <StrictMode>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <RouterProvider router={router} context={{ queryClient }} />
        <ToastProvider />
        {/* Mounted once, app-wide: keeps the Gleap widget's identity in sync
            with login/logout so support tickets don't leak across sessions
            or users on the same browser (see the component's own doc
            comment). */}
        <GleapIdentitySync />
        {/* Mounted once, app-wide: any locked feature's Upgrade CTA and the
            040005 backstop in lib/query-client.ts both open this one dialog. It
            renders null (and runs no billing query) until something opens it. */}
        <UpgradePromptDialog />
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </HelmetProvider>
  </StrictMode>,
);
